import {
  collection,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  setDoc,
  enableNetwork
} from 'firebase/firestore';
import { db } from '../config/firebase';

// Helper to check if error is an offline error
const isOfflineError = (error) => {
  return error.message?.includes('offline') || 
         error.code === 'unavailable' ||
         error.code === 'failed-precondition';
};

// Try to reconnect Firestore if offline
export const reconnectFirestore = async () => {
  try {
    await enableNetwork(db);
    console.log('Firestore network re-enabled');
    return true;
  } catch (error) {
    console.warn('Could not re-enable Firestore network:', error.message);
    return false;
  }
};

// Add a new document to collection
export const addDocument = async (collectionName, data, retryCount = 0) => {
  try {
    const docRef = await addDoc(collection(db, collectionName), data);
    return docRef.id;
  } catch (error) {
    // If offline error and haven't retried yet, try to reconnect and retry once
    if (isOfflineError(error) && retryCount < 1) {
      console.warn('Firestore offline, attempting to reconnect...');
      const reconnected = await reconnectFirestore();
      if (reconnected) {
        // Wait a moment for connection to stabilize
        await new Promise(resolve => setTimeout(resolve, 1000));
        return addDocument(collectionName, data, retryCount + 1);
      }
    }
    throw error;
  }
};

// Get document by ID
export const getDocument = async (collectionName, docId) => {
  try {
    const docRef = doc(db, collectionName, docId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
  } catch (error) {
    if (isOfflineError(error)) {
      console.warn(`Could not get document ${docId} - Firestore is offline`);
      return null;
    }
    throw error;
  }
};

// Get all documents from collection
export const getAllDocuments = async (collectionName) => {
  try {
    const querySnapshot = await getDocs(collection(db, collectionName));
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    if (isOfflineError(error)) {
      console.warn(`Could not get documents from ${collectionName} - Firestore is offline`);
      return [];
    }
    throw error;
  }
};

// Update document (or create if doesn't exist)
export const updateDocument = async (collectionName, docId, data, retryCount = 0) => {
  try {
    const docRef = doc(db, collectionName, docId);
    
    // Check if document exists first
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      // Update existing document
      await updateDoc(docRef, data);
    } else {
      // Create new document with the specified ID
      await setDoc(docRef, data);
    }
  } catch (error) {
    // If offline error and haven't retried yet, try to reconnect and retry once
    if (isOfflineError(error) && retryCount < 1) {
      console.warn('Firestore offline during update, attempting to reconnect...');
      const reconnected = await reconnectFirestore();
      if (reconnected) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return updateDocument(collectionName, docId, data, retryCount + 1);
      }
    }
    throw error;
  }
};

// Delete document by ID
export const deleteDocument = async (collectionName, docId) => {
  try {
    const docRef = doc(db, collectionName, docId);
    await deleteDoc(docRef);
  } catch (error) {
    throw error;
  }
};

// Query documents by field
export const queryDocuments = async (collectionName, field, operator, value) => {
  try {
    const q = query(collection(db, collectionName), where(field, operator, value));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    if (isOfflineError(error)) {
      console.warn(`Could not query ${collectionName} - Firestore is offline`);
      return [];
    }
    throw error;
  }
}; 